/**
 * Paste a screenshot straight onto a form.
 *
 * Print Screen → Ctrl+V is how people attach a screenshot. Saving the image to
 * a file and then finding it in a picker — or dragging it in — is the fallback,
 * not the habit, and it is the step at which most people give up and describe
 * the error in prose instead. So every surface that accepts files accepts a
 * pasted one.
 *
 * ## Where the handler goes, and why it is not on the uploader
 *
 * A `paste` event is dispatched at the focused element. Nobody pastes with
 * focus on the attach button: they paste mid-sentence, with the caret in the
 * description box, halfway down the form. A handler bound to the uploader would
 * therefore essentially never fire.
 *
 * `paste` bubbles, so the handler belongs on the element that *encloses the
 * whole surface* — the `<form>` in most cases, the card's content wrapper on
 * the emergency page, which has no form element. That is one listener per
 * surface, covering every field in it, with no window-level listener and so no
 * risk of a paste in one part of the app landing in another part's uploader.
 *
 * Deliberately *not* a window/document listener (the shape `useFileDropGuard`
 * uses): a missed *drop* is destructive — the browser navigates away and the
 * half-written ticket is gone — so it has to be caught app-wide. A paste that
 * lands nowhere costs nothing, so the narrower scope is free.
 *
 * ## One handler wins
 *
 * The hook takes the files, then calls `stopPropagation()`. Nested surfaces
 * (an `ATTACHMENT` offering parameter sitting inside the service-request form)
 * therefore resolve innermost-first: the inner boundary claims the image and
 * the outer one never sees the event. Same cooperation the drop zone has with
 * the drop guard, expressed with the event model rather than a registry.
 *
 * ## Pasting text keeps working
 *
 * Nothing is intercepted unless the clipboard actually carries an image *file*.
 * `clipboardData.files` is empty for a normal text paste, so text falls through
 * to the browser untouched.
 *
 * `preventDefault()` is narrower still: it is only called when the clipboard
 * has no text at all. A clipboard carrying both (copying a region out of Excel,
 * say) pastes its text into the field as usual *and* attaches the image —
 * cancelling the event there would silently swallow half of what the user
 * copied.
 */

import {
  type ClipboardEvent as ReactClipboardEvent,
  type RefObject,
  useCallback,
  useRef,
} from "react";

import { logger } from "@/lib/logger";

import type { AttachmentUploaderHandle } from "./attachment-uploader";

/**
 * The image files on a clipboard, named so a ticket can hold more than one.
 *
 * A pasted bitmap arrives nameless or as `image.png` — every single time — so
 * four screenshots on one ticket are four files called `image.png` and the
 * technician reading it cannot tell them apart or refer to one. Anything that
 * came from a real file keeps the name it had.
 *
 * Exported for the uploader's own consumers and for tests; the hook below is
 * the normal way in.
 */
/**
 * The file extension matching a clipboard image's real MIME type.
 *
 * Named `.png` regardless of type until 2026-09-16, which is wrong in the one
 * case it matters: the server checks an upload's actual content against its
 * allowlist, so a JPEG wearing a `.png` name is a mismatch waiting to be
 * rejected for a reason nobody could read off the filename. Falls back to
 * `.png` for an unrecognised image type, which is what a pasted bitmap almost
 * always is.
 */
function extensionFor(mimeType: string): string {
  const known: Record<string, string> = {
    "image/png": ".png",
    "image/jpeg": ".jpg",
    "image/gif": ".gif",
    "image/webp": ".webp",
    "image/bmp": ".bmp",
    "image/svg+xml": ".svg",
  };
  return known[mimeType.toLowerCase()] ?? ".png";
}

export function clipboardImageFiles(data: DataTransfer | null): File[] {
  if (data === null) {
    return [];
  }
  const images = Array.from(data.files).filter((file) => file.type.startsWith("image/"));
  if (images.length === 0) {
    return [];
  }
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  return images.map((file, index) =>
    file.name === "" || file.name === "image.png"
      ? new File(
          [file],
          `screenshot-${stamp}${images.length > 1 ? `-${index + 1}` : ""}${extensionFor(file.type)}`,
          {
            type: file.type,
          },
        )
      : file,
  );
}

export interface PasteToAttachOptions {
  /**
   * Ignore pastes while the surface is locked (submitting, streaming). Matches
   * whatever is passed to the uploader's own `disabled`.
   */
  disabled?: boolean;
}

export interface PasteToAttach {
  /**
   * Hand to the `AttachmentUploader` that should receive pasted images. Exactly
   * one per surface — which is what makes the winner explicit at the call site
   * rather than a mount-order accident when a surface has two uploaders.
   */
  ref: RefObject<AttachmentUploaderHandle | null>;
  /**
   * Put on the element enclosing the whole surface — the `<Form>`, or the
   * wrapper around the fields where there is no form.
   */
  onPaste: (event: ReactClipboardEvent<Element>) => void;
}

export function usePasteToAttach({ disabled = false }: PasteToAttachOptions = {}): PasteToAttach {
  const ref = useRef<AttachmentUploaderHandle>(null);
  // Read through a ref so `onPaste` stays referentially stable and does not
  // re-attach on every keystroke that flips a pending flag.
  const disabledRef = useRef(disabled);
  disabledRef.current = disabled;

  const onPaste = useCallback((event: ReactClipboardEvent<Element>) => {
    if (disabledRef.current) {
      return;
    }
    const files = clipboardImageFiles(event.clipboardData);
    if (files.length === 0) {
      // A plain text paste, and every paste on a surface whose uploader is not
      // mounted (a comment box that was not given attachments). Untouched.
      return;
    }
    if (ref.current === null) {
      return;
    }
    // Claim it before any enclosing surface sees it.
    event.stopPropagation();
    // Only when there is nothing else on the clipboard to paste — see above.
    if (event.clipboardData.getData("text/plain") === "") {
      event.preventDefault();
    }
    logger.debug("[attachments] pasted images", files.length);
    ref.current.addFiles(files);
  }, []);

  return { ref, onPaste };
}
